// @flow

// force flow to type check this file - https://github.com/zapier/zapier/pull/31127
/* eslint-disable-next-line no-unused-vars */
import React from 'react';

import { keyframes } from '@emotion/core';
import { type ColorName, Colors } from '@zapier/design-system';
import { isolateComponent, labelStyles } from '@zapier/common-utils';

/** @jsx isolateComponent */

type Props = {
  /** Accessibility label for the node */
  ariaLabel: string,
  /** Color of the dots (can use theme color strings) */
  color: ColorName,
  /** Diameter of the dots */
  dotSize: number,
};

const dotCount = 3;
const animationDuration = 500;

const dotPulse = keyframes`
  0% { transform: scale(1); }
  100% { transform: scale(0.7); }
`;

const Styles = labelStyles(__filename, {
  root: {
    overflow: 'visible', // Prevent antialiasing clipping
  },
  dot: {
    animation: `${dotPulse} ${animationDuration}ms ease-in-out alternate
        infinite`,
  },
});

/**
 * Renders a spinner that indicates that something async is happening.
 *
 * Tags: core-ui
 *
 * @deprecated Consider https://zapier-ui.k8s.zapier.com/#/Components/Display/Spinner instead.
 */

const AsyncIndicator = ({ ariaLabel, color, dotSize }: Props) => {
  const r = dotSize / 2;
  // Total height of nodes is same as `dotSize`
  const height = dotSize;
  // Total width is `dotCount * dotSize` for total width of the dots,
  // times 1.5 to add the gaps in, minus `r` to subtract the final
  // gap at the end which is unnecessary.
  const width = dotCount * dotSize * 1.5 - r;
  const dots = Array.from({ length: dotCount }).map((n, i) => {
    // Multiply by 3 because we want each dot to be one `r`
    // apart from the other dots. Add 1 because this is a `cx`
    // value, meaning half the dot will be to the left of `cx`,
    // so we need to start one `r` to the right.
    const cx = (i * 3 + 1) * r;
    const cy = r;
    const style = {
      // Divide `animationDuration` into `dotCount` so each dot
      // animates within the `animationDuration` timeframe, otherwise
      // multiple dots might grow at the same time which looks weird.
      // Multiply by `i` since each dot needs to begin animating after
      // the previous dot.
      animationDelay: `${i * (animationDuration / dotCount)}ms`,
      transformOrigin: `${cx}px ${cy}px`,
    };
    return (
      <circle
        css={Styles.dot}
        cx={cx}
        cy={cy}
        fill={color === 'inherit' ? 'currentColor' : Colors[color] || color}
        key={i}
        r={r}
        style={style}
      />
    );
  });

  return (
    <svg
      aria-hidden="false"
      aria-label={ariaLabel}
      css={Styles.root}
      height={height}
      width={width}
    >
      {dots}
    </svg>
  );
};

AsyncIndicator.defaultProps = {
  ariaLabel: 'Loading',
  color: 'grey500',
  dotSize: 6,
};

export default AsyncIndicator;
